import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ComuniCombobox } from "../components/ComuniCombobox";
import { toast } from "sonner";
import { Mail, Lock, User, MapPin, Loader2, ArrowRight, CheckCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RegisterPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    comune: "",
    email: "",
    password: ""
  });
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Nome richiesto";
    if (!formData.comune) newErrors.comune = "Comune richiesto";
    if (!formData.email.trim()) newErrors.email = "Email richiesta";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = "Email non valida";
    if (!formData.password) newErrors.password = "Password richiesta";
    else if (formData.password.length < 6) newErrors.password = "Minimo 6 caratteri";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      // Registrazione flash - utente senza tipo definito (pending)
      const response = await axios.post(`${API}/auth/register`, {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        user_type: "pending", // Tipo temporaneo, sceglierà dopo
        comune_residenza: formData.comune
      });

      if (response.data.token) {
        // Login automatico
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("user", JSON.stringify(response.data.user));
        
        toast.success("Registrazione completata!");
        
        // Reindirizza alla selezione ruolo
        navigate("/select-role");
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Errore durante la registrazione";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="register-page">
      <Navbar />
      
      <div className="max-w-md mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#DA291C]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <User className="w-8 h-8 text-[#DA291C]" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            Crea il tuo account
          </h1>
          <p className="text-slate-600">
            Inizia in pochi secondi, scegli il tuo percorso dopo
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6 sm:p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Nome */}
            <div>
              <Label htmlFor="name" className="text-slate-700 font-medium">
                Nome e Cognome *
              </Label>
              <div className="relative mt-1.5">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="name"
                  type="text"
                  placeholder="Mario Rossi"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={`pl-10 h-12 bg-white border-slate-300 text-black ${errors.name ? 'border-red-500' : ''}`}
                  data-testid="register-name"
                />
              </div>
              {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
            </div>

            {/* Comune */}
            <div>
              <Label htmlFor="comune" className="text-slate-700 font-medium">
                Comune di Residenza *
              </Label>
              <div className="mt-1.5">
                <ComuniCombobox
                  value={formData.comune}
                  onValueChange={(value) => setFormData({ ...formData, comune: value })}
                  className={errors.comune ? 'border-red-500' : ''}
                />
              </div>
              {errors.comune && <p className="text-red-500 text-sm mt-1">{errors.comune}</p>}
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="email" className="text-slate-700 font-medium">
                Email *
              </Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="mario@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={`pl-10 h-12 bg-white border-slate-300 text-black ${errors.email ? 'border-red-500' : ''}`}
                  data-testid="register-email"
                />
              </div>
              {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
            </div>

            {/* Password */}
            <div>
              <Label htmlFor="password" className="text-slate-700 font-medium">
                Password *
              </Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="Minimo 6 caratteri"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className={`pl-10 h-12 bg-white border-slate-300 text-black ${errors.password ? 'border-red-500' : ''}`}
                  data-testid="register-password"
                />
              </div>
              {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#DA291C] hover:bg-[#B91C1C] text-white font-semibold text-base"
              data-testid="register-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Creazione account...
                </>
              ) : (
                <>
                  Continua
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </form>

          {/* Benefits */}
          <div className="mt-6 pt-6 border-t border-slate-100">
            <div className="flex items-center gap-2 text-sm text-slate-600 mb-2">
              <CheckCircle className="w-4 h-4 text-emerald-500" />
              Registrazione gratuita
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600 mb-2">
              <CheckCircle className="w-4 h-4 text-emerald-500" />
              Nessun impegno
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <CheckCircle className="w-4 h-4 text-emerald-500" />
              Dati protetti (LPD Svizzera)
            </div>
          </div>
        </div>

        {/* Login Link */}
        <p className="text-center text-slate-600 mt-6">
          Hai già un account?{" "}
          <Link to="/login" className="text-[#DA291C] font-semibold hover:underline">
            Accedi
          </Link>
        </p>
      </div>
    </div>
  );
}
